import React from 'react';
import { cn } from '@/lib/utils';

/**
 * PersistentBottomBar - Refined fixed bottom action zone
 * 
 * A compact, gentle action bar anchored to the bottom of the screen.
 * Part of the universal 3-zone navigation pattern.
 * 
 * Refinements (Feb 2026):
 * - Reduced height by ~30% for a lighter feel
 * - Semi-transparent background with rounded top corners
 * - Smaller, gentler primary buttons (44-48px)
 * - Soft gradient fade for seamless blend
 * 
 * Usage:
 * <PersistentBottomBar>
 *   <PrimaryButton size="md">Continue</PrimaryButton>
 * </PersistentBottomBar>
 */
export const PersistentBottomBar = ({ 
    children, 
    className,
    showGradient = true,
    padding = 'default', // 'default' | 'compact' | 'minimal' | 'inline'
    layout = 'stacked' // 'stacked' | 'inline'
}) => {
    // Reduced padding for 25-35% height reduction
    const paddingClasses = {
        default: 'px-5 pt-3 pb-4',
        compact: 'px-4 pt-2.5 pb-3',
        minimal: 'px-4 pt-2 pb-3',
        inline: 'px-4 pt-2 pb-2'
    };

    return (
        <div 
            className={cn(
                'fixed bottom-0 left-0 right-0 z-50',
                'safe-area-inset-bottom',
                className
            )}
            data-testid="persistent-bottom-bar"
        >
            {/* Subtle gradient fade for smooth transition */}
            {showGradient && (
                <div className={cn(
                    "bg-gradient-to-t from-[#FBF6EE]/80 to-transparent pointer-events-none",
                    padding === 'inline' ? 'h-3' : 'h-6'
                )} />
            )}
            
            {/* Action bar content - rounded top corners, semi-transparent */}
            <div 
                className={cn(
                    'bg-[#FBF6EE]/85 backdrop-blur-lg',
                    'border-t border-[#F0E8DA]/60',
                    'rounded-t-2xl',
                    'shadow-[0_-4px_20px_rgba(0,0,0,0.04)]',
                    paddingClasses[padding]
                )}
            >
                <div className="max-w-lg mx-auto">
                    <div className={cn(
                        layout === 'inline' 
                            ? 'flex items-center justify-center gap-4' 
                            : 'space-y-2'
                    )}>
                        {children}
                    </div>
                </div>
            </div>
        </div>
    );
};

/**
 * BottomBarSpacer - Dynamic spacer to prevent content being hidden
 * 
 * IMPORTANT: Add this at the END of every page's content area.
 * Height must be >= action bar height to ensure no content is obscured.
 * 
 * Heights adjusted for the refined, compact bar:
 * - default: 100px (standard single-button bar)
 * - compact: 80px (minimal bar)
 * - extended: 130px (bar with secondary action)
 * - breathwork: 120px (breathwork tools with animations)
 */
export const BottomBarSpacer = ({ 
    height = 'default' // 'default' | 'compact' | 'extended' | 'breathwork' | 'inline' | number
}) => {
    // Adjusted heights for refined bar (~30% smaller)
    const heightClasses = {
        default: 'h-[100px]',
        compact: 'h-[80px]',
        extended: 'h-[130px]',
        breathwork: 'h-[120px]',
        inline: 'h-[60px]'
    };

    const heightClass = typeof height === 'number' 
        ? undefined 
        : heightClasses[height];
    
    const heightStyle = typeof height === 'number' 
        ? { height: `${height}px` } 
        : undefined;

    return (
        <div 
            className={cn('w-full flex-shrink-0', heightClass)}
            style={heightStyle}
            aria-hidden="true"
            data-testid="bottom-bar-spacer"
        />
    );
};

/**
 * ContentWithBottomBar - Wrapper that automatically adds proper padding
 * 
 * Use this to wrap page content that has a PersistentBottomBar.
 * Ensures content never sits behind the bar.
 */
export const ContentWithBottomBar = ({ 
    children, 
    className,
    spacerHeight = 'default'
}) => {
    return (
        <div className={cn('flex flex-col min-h-full', className)}>
            <div className="flex-1">
                {children}
            </div>
            <BottomBarSpacer height={spacerHeight} />
        </div>
    );
};

export default PersistentBottomBar;
